/**
 * Product page engagement tracker (SDD_PRODUCT_ANALYTICS.md §3, §6.2, §6.3).
 *
 * Framework-free. Measures per-session engagement on a product detail page:
 * - scroll milestones (25/50/75/100% of content, comments/footer excluded)
 * - active time (tab visible + user not idle for 30s)
 * - website CTA clicks (once per placement per session)
 *
 * Current sink: PostHog mirroring (`product_scroll_milestone`, `product_cta_click`,
 * `product_session_end`). First-party ingest (§5.1) plugs in here when the
 * backend endpoint ships. All capture is fire-and-forget — a blocked or
 * unconfigured PostHog must never affect the page.
 */

import posthog from 'posthog-js'

export type CtaPlacement = 'hero' | 'sidebar'

export interface ProductTracker {
  setSelfView(selfView: boolean): void
  trackCtaClick(placement: CtaPlacement): void
  /** Ends the session and sends `product_session_end`. Idempotent. */
  destroy(): void
}

const MILESTONES = [25, 50, 75, 100] as const
const IDLE_MS = 30_000
const MAX_ACTIVE_MS = 30 * 60 * 1000
const ACTIVITY_EVENTS = ['scroll', 'wheel', 'pointermove', 'pointerdown', 'keydown', 'touchstart'] as const

function safeCapture(event: string, properties: Record<string, unknown>, beacon = false) {
  try {
    posthog.capture(event, properties, beacon ? { transport: 'sendBeacon' } : undefined)
  } catch {
    // Analytics must never surface an error to a visitor (§6.2).
  }
}

function newSessionId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `s-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
  }
}

export function createProductTracker(
  slug: string,
  /** Marker element after the last content section, before comments (§3.2). */
  getContentEnd: () => HTMLElement | null,
): ProductTracker {
  const sessionId = newSessionId()
  let selfView = false
  let ended = false

  const firedMilestones = new Set<number>()
  const firedCtaPlacements = new Set<CtaPlacement>()

  // Active-time clock: accumulate on run→pause transitions, no interval (§6.2).
  let activeMs = 0
  let segmentStart: number | null = document.visibilityState === 'visible' ? Date.now() : null
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let idle = false

  const pauseClock = () => {
    if (segmentStart !== null) {
      activeMs += Date.now() - segmentStart
      segmentStart = null
    }
  }

  const resumeClock = () => {
    if (segmentStart === null && !idle && document.visibilityState === 'visible') {
      segmentStart = Date.now()
    }
  }

  const totalActiveSeconds = () => {
    const running = segmentStart !== null ? Date.now() - segmentStart : 0
    return Math.round(Math.min(activeMs + running, MAX_ACTIVE_MS) / 1000)
  }

  const armIdleTimer = () => {
    if (idleTimer !== null) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      idle = true
      pauseClock()
    }, IDLE_MS)
  }

  const onActivity = () => {
    if (ended) return
    if (idle) {
      idle = false
      resumeClock()
    }
    armIdleTimer()
  }

  const onVisibilityChange = () => {
    if (ended) return
    if (document.visibilityState === 'hidden') {
      pauseClock()
    } else {
      resumeClock()
      armIdleTimer()
    }
  }

  const baseProps = () => ({
    product_slug: slug,
    product_session_id: sessionId,
    self_view: selfView,
  })

  // Scroll depth against content height, not page height (§3.2). Falls back to
  // full document height until the content-end marker is mounted.
  const checkScroll = () => {
    if (ended || firedMilestones.size === MILESTONES.length) return
    const endEl = getContentEnd()
    const contentBottom = endEl
      ? endEl.getBoundingClientRect().bottom + window.scrollY
      : document.documentElement.scrollHeight
    if (contentBottom <= 0) return
    const progress = ((window.scrollY + window.innerHeight) / contentBottom) * 100
    for (const milestone of MILESTONES) {
      if (progress >= milestone && !firedMilestones.has(milestone)) {
        firedMilestones.add(milestone)
        safeCapture('product_scroll_milestone', { ...baseProps(), milestone })
      }
    }
  }

  let scrollRafPending = false
  const onScroll = () => {
    if (scrollRafPending) return
    scrollRafPending = true
    requestAnimationFrame(() => {
      scrollRafPending = false
      checkScroll()
    })
  }

  // Content height shifts as images load — re-check milestones when it does.
  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onScroll) : null
  resizeObserver?.observe(document.body)

  const end = () => {
    if (ended) return
    ended = true
    pauseClock()
    if (idleTimer !== null) clearTimeout(idleTimer)
    safeCapture(
      'product_session_end',
      {
        ...baseProps(),
        active_seconds: totalActiveSeconds(),
        max_scroll_milestone: Math.max(0, ...firedMilestones),
      },
      true,
    )
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    window.removeEventListener('pagehide', end)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, onActivity)
    }
    resizeObserver?.disconnect()
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  window.addEventListener('pagehide', end)
  document.addEventListener('visibilitychange', onVisibilityChange)
  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, onActivity, { passive: true })
  }
  armIdleTimer()
  // Pages shorter than the viewport fire their milestones immediately (§3.2).
  checkScroll()

  return {
    setSelfView(value: boolean) {
      selfView = value
    },
    trackCtaClick(placement: CtaPlacement) {
      if (ended || firedCtaPlacements.has(placement)) return
      firedCtaPlacements.add(placement)
      safeCapture('product_cta_click', { ...baseProps(), placement })
    },
    destroy: end,
  }
}
